import launcherCss from './launcher.css?inline';
import { createLauncher, type Launcher, type TeaserAction } from './launcher';
import { watchBottomBars } from './bottomBars';
import { watchKeyboard } from './keyboard';
import { launcherIcon, launcherLabel, markTeaserSeen, pickTeaser, readSavedVehicle, snoozeTeaser, teaserAllowed } from './teaser';
import type { PanelLoader } from './panelLoader';
import { getMeasurementState, type MeasurementHandle, type MeasurementHooks, type SessionTenant } from './measurement';
import { startPageViews, type PageViewHandle } from './pageView';
import { Emitter } from '../shared/emitter';
import { getLauncherStrings, resolveLocale } from '../shared/locale';
import { clearReopen, isDesktopViewport, takeReopen } from '../shared/reopen';
import { loadState, saveState } from '../shared/storage';
import { resolveTheme, themeToCssVars } from '../shared/theme';
import type { TenantTheme } from '../types/contracts';
import type {
  AICommerceApi,
  AICommerceConfig,
  AICommerceEvents,
  PanelController,
} from '../types/sdk';

export const VERSION = '0.1.0';
export const HOST_ID = 'aicommerce-widget';
const PANEL_ID = 'aca-panel';
/** Launcher choreography: the bubble appears after the visitor had time to look at the page, and leaves on its own. */
const TEASER_DELAY_MS = 5000;
const TEASER_STAY_MS = 16000;
const COMPACT_SCROLL_PX = 240;
/** Closer than this to the end of the page, the bubble would sit on the footer (legal line, the shop's credits). */
const PAGE_END_PX = 160;
/**
 * Fired on `window` (cancelable) by the bubble's «choose my car»: a store theme with its own car picker opens it and
 * calls `preventDefault()`; otherwise the reply goes to the chat.
 */
export const PICK_VEHICLE_EVENT = 'aicommerce:pick-vehicle';

/** Results page of the car saved in the configurator, when the configurator script is on the page. */
function savedVehicleResultsUrl(): string | null {
  const fitment = (window as unknown as { AICommerceFitment?: { getVehicle(): { id: string } | null; resultsUrl?(id: string): string | null } }).AICommerceFitment;
  try {
    const v = fitment?.getVehicle();
    return v ? (fitment?.resultsUrl?.(v.id) ?? null) : null;
  } catch {
    return null;
  }
}

/** The visitor is at the end of the page; `t` is the element that scrolled (themes that scroll an inner wrapper). */
function nearPageEnd(t: EventTarget | null): boolean {
  if (t instanceof Element && t.scrollHeight > t.clientHeight + 1) return t.scrollHeight - t.scrollTop - t.clientHeight < PAGE_END_PX;
  const page = document.documentElement.scrollHeight;
  // a page that fits the screen has no end to reach: the bubble behaves as before
  return page > window.innerHeight && page - window.scrollY - window.innerHeight < PAGE_END_PX;
}

/** CONTRACTS §J.4 — what `AICommerce.getAttribution()` returns (read by the Shopify/Woo integrations at checkout). */
export interface WidgetAttribution {
  visitor_id: string | null;
  session_id: string | null;
  conversation_id: string | null;
  arm: 'treatment' | 'holdout' | null;
  experiment_id: string | null;
}

/** CONTRACTS §K.2: conversation + token carried by the offline e-mail link (`#aca-conversation=…&aca-token=…`). */
export interface ResumeLink {
  conversation_id: string;
  token: string;
}

/** Public API plus the §J.4 / §K.2 additive methods. */
export type AICommerceApiWithAttribution = AICommerceApi & {
  getAttribution(): WidgetAttribution;
  /**
   * §K.2: opens the panel on a conversation. With `token` (resume link) the conversation is re-attached to this
   * browser's session through `POST /v1/chat/resume`. Resolves to false when it could not be opened.
   */
  openConversation(conversationId: string, opts?: { token?: string }): Promise<boolean>;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RESUME_KEYS = ['aca-conversation', 'aca-token'];

/**
 * Reads the resume link from `location.hash` and strips it from the address bar (`history.replaceState`, other hash
 * parameters are kept), so the token is not left in the URL, in bookmarks or in a shared link.
 */
export function takeResumeLink(loc: Location = window.location, hist: History = window.history): ResumeLink | null {
  const raw = loc.hash.replace(/^#/, '');
  if (!raw.includes('aca-conversation=')) return null;
  const params = new URLSearchParams(raw);
  const conversation_id = params.get('aca-conversation') ?? '';
  const token = params.get('aca-token') ?? '';
  for (const k of RESUME_KEYS) params.delete(k);
  const rest = params.toString();
  try {
    hist.replaceState(hist.state, '', `${loc.pathname}${loc.search}${rest ? `#${rest}` : ''}`);
  } catch {
    /* sandboxed iframes may forbid replaceState: the link still works */
  }
  return UUID_RE.test(conversation_id) && /^[A-Za-z0-9_.-]{16,200}$/.test(token) ? { conversation_id, token } : null;
}

interface ShopifyRootWindow {
  Shopify?: { routes?: { root?: string } };
}

/** Shopify cart attributes (`_aica_*`, hidden in the storefront, copied to the order as `note_attributes`). */
export function cartAttributesOf(a: WidgetAttribution): Record<string, string> {
  const out: Record<string, string> = {};
  if (a.visitor_id) out._aica_vid = a.visitor_id;
  if (a.session_id) out._aica_sid = a.session_id;
  if (a.conversation_id) out._aica_conv = a.conversation_id;
  if (a.arm) out._aica_arm = a.arm;
  if (a.experiment_id) out._aica_exp = a.experiment_id;
  // consent revoked (or never given): '' clears an id written earlier (cart/update.js merges attributes)
  if (!a.visitor_id && Object.keys(out).length) out._aica_vid = '';
  return out;
}

export interface SdkOptions {
  /** Function that resolves the panel module (script injection in production, dynamic import in dev/tests). */
  panelLoader: (config: AICommerceConfig) => PanelLoader;
  /**
   * Holdout/measurement bootstrap (CONTRACTS §J.2): decides whether and when the launcher is mounted. Without it
   * (tests, embedders that opt out) the launcher mounts synchronously in `init()`.
   */
  measurement?: (config: AICommerceConfig, hooks: MeasurementHooks) => MeasurementHandle;
}

function validate(config: AICommerceConfig): string | null {
  if (!config || typeof config !== 'object') return 'config object is required';
  if (!config.tenantKey || typeof config.tenantKey !== 'string') return 'tenantKey is required';
  if (!config.apiBase || typeof config.apiBase !== 'string') return 'apiBase is required';
  if (config.platform && !['shopify', 'woocommerce', 'custom'].includes(config.platform)) return 'platform must be shopify | woocommerce | custom';
  if (config.platform === 'custom' && config.onAddToCart && typeof config.onAddToCart !== 'function') return 'onAddToCart must be a function';
  return null;
}

export function createSdk(options: SdkOptions): AICommerceApiWithAttribution {
  const emitter = new Emitter<AICommerceEvents>();
  let config: AICommerceConfig | null = null;
  let host: HTMLElement | null = null;
  let root: HTMLDivElement | null = null;
  let shadow: ShadowRoot | null = null;
  let launcher: Launcher | null = null;
  let panel: PanelController | null = null;
  let panelPromise: Promise<PanelController> | null = null;
  let isOpen = false;
  let loadPanel: PanelLoader | null = null;
  let measurement: MeasurementHandle | null = null;
  /** §K.2: resume link found at init, consumed once the launcher is mounted (never in the holdout arm). */
  let pendingResume: ResumeLink | null = null;
  /** The visitor arrived here through a same-tab link of the chat, on desktop: open the panel once (shared/reopen). */
  let pendingReopen = false;
  /** §S.2: reports the page of a visitor who has a conversation. */
  let pageViews: PageViewHandle | null = null;
  /** `document.title` before the panel prefixed it with the unread count (so `page_view` carries the real title). */
  let cleanTitle: string | null = null;
  let launcherTeardown: Array<() => void> = [];
  /** Launcher lifted above the site's bottom bars (sticky add-to-cart on phones). */
  let stopBottomBars: (() => void) | null = null;
  /** Phone panel kept above the virtual keyboard. */
  let stopKeyboard: (() => void) | null = null;

  const applyTheme = (sessionTheme?: TenantTheme) => {
    if (!root || !config) return;
    const t = resolveTheme(sessionTheme, config.theme);
    root.setAttribute('style', themeToCssVars(t));
    root.classList.toggle('aca-left', t.position === 'bottom-left');
  };

  /** Tenant theme (cached from the last session) under the integrator's overrides. */
  const launcherTheme = (): TenantTheme => ({ ...(config ? (loadState(config.tenantKey).theme as TenantTheme | undefined) : undefined), ...config?.theme });
  const launcherStrings = () => getLauncherStrings(resolveLocale(config?.locale, config ? loadState(config.tenantKey).tenant_locale : undefined));
  const refreshLauncher = () => {
    if (!launcher) return;
    launcher.setLabel(launcherLabel(launcherTheme(), launcherStrings()));
    launcher.setIcon(launcherIcon(launcherTheme()));
  };
  const onTenant = (tenant: SessionTenant) => {
    if (!config) return;
    if (tenant.theme && typeof tenant.theme === 'object') {
      saveState(config.tenantKey, { theme: tenant.theme });
      applyTheme(tenant.theme);
    }
    refreshLauncher();
  };

  /**
   * What makes the launcher noticed without nagging: the label folds to the icon once the visitor scrolls (and, on a
   * phone, when the bubble takes its place); the bubble for this page shows once per tab session, then leaves.
   */
  const scheduleNudges = (cfg: AICommerceConfig) => {
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    let lastScroller: EventTarget | null = null;
    // at the end of the page the bubble leaves (and does not show), so the footer stays readable
    const onPageEnd = (e: Event) => {
      lastScroller = e.target;
      if (nearPageEnd(e.target)) launcher?.hideTeaser();
    };
    document.addEventListener('scroll', onPageEnd, { passive: true, capture: true });
    // Some themes (Shopify Horizon among them) scroll an inner wrapper, not the window: listen in capture phase so
    // any scroller counts, and read the scrolled element's offset when the window itself has not moved.
    const onScroll = (e: Event) => {
      const t = e.target as Element | Document | null;
      const inner = t && t !== document && 'scrollTop' in t ? (t as Element).scrollTop : 0;
      if (Math.max(window.scrollY, inner) < COMPACT_SCROLL_PX) return;
      launcher?.setCompact(true);
      document.removeEventListener('scroll', onScroll, true);
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    timers.push(setTimeout(() => {
      if (!launcher || isOpen || pendingResume || !teaserAllowed(cfg.tenantKey) || nearPageEnd(lastScroller)) return;
      const teaser = pickTeaser(launcherTheme(), launcherStrings(), cfg.context?.page_type, readSavedVehicle());
      if (!teaser) return;
      if (!isDesktopViewport()) launcher.setCompact(true);
      launcher.showTeaser(teaser);
      markTeaserSeen(cfg.tenantKey);
      timers.push(setTimeout(() => launcher?.hideTeaser(), TEASER_STAY_MS));
    }, TEASER_DELAY_MS));
    launcherTeardown = [
      () => document.removeEventListener('scroll', onScroll, true),
      () => document.removeEventListener('scroll', onPageEnd, true),
      () => timers.forEach(clearTimeout),
    ];
  };

  const ensurePanel = (): Promise<PanelController> => {
    if (panel) return Promise.resolve(panel);
    if (panelPromise) return panelPromise;
    if (!config || !shadow || !root || !loadPanel) return Promise.reject(new Error('AICommerce.init() must be called first'));
    const cfg = config;
    const sr = shadow;
    const rootEl = root;
    launcher?.setBusy(true);
    panelPromise = loadPanel()
      .then((mod) => {
        const mountNode = document.createElement('div');
        mountNode.id = PANEL_ID;
        rootEl.appendChild(mountNode);
        panel = mod.mount({
          config: cfg,
          mountNode,
          shadowRoot: sr,
          emit: (e, p) => emitter.emit(e, p),
          requestClose: () => api.close(),
          onTheme: (theme) => {
            saveState(cfg.tenantKey, { theme });
            applyTheme(theme);
            refreshLauncher();
          },
          onUnread: (count) => {
            // called before the panel touches `document.title`
            if (count > 0) cleanTitle ??= document.title;
            else cleanTitle = null;
            launcher?.setUnread(count);
          },
        });
        return panel;
      })
      .catch((err: unknown) => {
        panelPromise = null;
        const message = err instanceof Error ? err.message : String(err);
        emitter.emit('error', { code: 'panel_load_failed', message });
        throw err;
      })
      .finally(() => launcher?.setBusy(false));
    return panelPromise;
  };

  const setOpenState = (open: boolean) => {
    isOpen = open;
    launcher?.setOpen(open);
    root?.classList.toggle('aca-open', open);
  };

  /** Shadow host + launcher (not called in the holdout arm). */
  function mountLauncher() {
    if (!config || host) return;
    host = document.createElement('div');
    host.id = HOST_ID;
    host.setAttribute('data-aicommerce', VERSION);
    // The host has no light-DOM children (everything lives in the shadow root): theme resets such as Dawn's
    // `div:empty { display: none }` would hide it, so the display is pinned inline with priority.
    host.style.setProperty('display', 'block', 'important');
    (config.container ?? document.body).appendChild(host);
    shadow = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = launcherCss;
    shadow.appendChild(style);
    root = document.createElement('div');
    root.className = 'aca-root';
    shadow.appendChild(root);

    const cached = loadState(config.tenantKey).theme as TenantTheme | undefined;
    applyTheme(cached);

    const strings = getLauncherStrings(resolveLocale(config.locale, loadState(config.tenantKey).tenant_locale));
    launcher = createLauncher(
      root,
      strings,
      PANEL_ID,
      () => {
        if (isOpen) api.close();
        else void api.open().catch(() => undefined);
      },
      () => {
        void ensurePanel().catch(() => undefined);
      },
      {
        // the bubble's question becomes the visitor's first message; a bubble without one just opens the chat
        onTeaser: (teaser) => void (teaser.message ? api.sendMessage(teaser.message) : api.open()).catch(() => undefined),
        onTeaserAction: (action: TeaserAction) => {
          if (action.do === 'pick_vehicle' && !window.dispatchEvent(new CustomEvent(PICK_VEHICLE_EVENT, { cancelable: true }))) return;
          const results = action.do === 'vehicle_results' ? savedVehicleResultsUrl() : null;
          if (results) return window.location.assign(results);
          void (action.message ? api.sendMessage(action.message) : api.open()).catch(() => undefined);
        },
        onTeaserDismiss: () => config && snoozeTeaser(config.tenantKey),
      },
    );
    refreshLauncher();
    if (typeof window !== 'undefined') {
      const r = root;
      stopBottomBars = watchBottomBars(window, r, host, () => r.classList.contains('aca-left'));
      stopKeyboard = watchKeyboard(window, r);
    }
    if (typeof window !== 'undefined' && !pendingResume && !pendingReopen) scheduleNudges(config);
    if (pendingResume) {
      const link = pendingResume;
      pendingResume = null;
      queueMicrotask(() => void api.openConversation(link.conversation_id, { token: link.token }));
    } else if (pendingReopen) {
      queueMicrotask(() => void api.open().catch(() => undefined));
    }
    pendingReopen = false;
  }

  /** Identity used for order attribution: measurement state (§J.2) + session/conversation persisted by the panel. */
  const getAttribution = (): WidgetAttribution => {
    const m = getMeasurementState();
    const stored = config ? loadState(config.tenantKey) : {};
    return {
      visitor_id: m.visitor_id ?? null,
      session_id: stored.session_id ?? m.session_id ?? null,
      conversation_id: stored.conversation_id ?? null,
      arm: m.experiment?.arm ?? null,
      experiment_id: m.experiment?.experiment_id ?? null,
    };
  };

  /**
   * Shopify: writes the `_aica_*` cart attributes via `POST /cart/update.js` (CONTRACTS §J.4) when the session opens, when a
   * conversation starts and after every add-to-cart (`force`: the cart may have been recreated). Skipped when nothing changed.
   */
  let lastCartAttributes = '';
  let attributeTeardown: Array<() => void> = [];
  const syncCartAttributes = async (force = false): Promise<void> => {
    if (!config || config.platform !== 'shopify' || typeof fetch !== 'function') return;
    const attributes = cartAttributesOf(getAttribution());
    const key = JSON.stringify(attributes);
    if (!Object.keys(attributes).length || (!force && key === lastCartAttributes)) return;
    lastCartAttributes = key;
    const root = (window as unknown as ShopifyRootWindow).Shopify?.routes?.root ?? '/';
    try {
      const res = await fetch(`${root.replace(/\/?$/, '/')}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes }),
      });
      if (!res.ok) lastCartAttributes = '';
    } catch {
      lastCartAttributes = ''; // retried on the next trigger
    }
  };
  const watchAttribution = () => {
    const sync = (force = false) => void syncCartAttributes(force);
    attributeTeardown = [
      emitter.on('ready', () => sync()),
      emitter.on('message', () => sync()),
      emitter.on('add_to_cart', (p) => {
        if (p.ok) sync(true);
      }),
    ];
    if (measurement) void measurement.ready.then(() => sync(), () => sync());
    else queueMicrotask(() => sync());
  };

  /**
   * §S.2 `page_view`: on every page load once the session is known (the bootstrap may drop a conversation that belongs
   * to an expired session), and again when the conversation of this browser changes on the current page: first
   * customer message answered (`message`), panel bootstrap (`ready`), a previous conversation reopened.
   */
  const watchPageViews = (cfg: AICommerceConfig) => {
    const handle = startPageViews(cfg, { getTitle: () => cleanTitle ?? document.title });
    pageViews = handle;
    const notify = () => handle.notify();
    attributeTeardown.push(emitter.on('ready', notify), emitter.on('message', notify), emitter.on('conversation:status', notify));
    if (measurement) void measurement.ready.then(notify, notify);
    else queueMicrotask(notify);
  };

  const api: AICommerceApiWithAttribution = {
    version: VERSION,

    init(cfg) {
      const problem = validate(cfg);
      if (problem) {
        console.error(`[AICommerce] init: ${problem}`);
        emitter.emit('error', { code: 'invalid_config', message: problem });
        return;
      }
      if (config) {
        console.warn('[AICommerce] already initialised; call destroy() first to re-init');
        return;
      }
      config = { platform: 'custom', ...cfg, apiBase: cfg.apiBase.replace(/\/+$/, '') };
      if (typeof window !== 'undefined') {
        pendingResume = takeResumeLink();
        // the mark is consumed by this page load whatever happens next: later pages never reopen on their own
        pendingReopen = takeReopen(config.tenantKey) && isDesktopViewport();
      }
      loadPanel = options.panelLoader(config);
      if (options.measurement) measurement = options.measurement(config, { mount: mountLauncher, isMounted: () => host !== null, tenant: onTenant });
      else mountLauncher();
      watchAttribution();
      if (typeof window !== 'undefined') watchPageViews(config);
    },

    getAttribution() {
      return getAttribution();
    },

    async open() {
      if (!config) {
        console.warn('[AICommerce] open() called before init()');
        return;
      }
      if (!host) return; // holdout arm (or arm not known yet): the assistant is not shown
      if (isOpen) return;
      setOpenState(true);
      try {
        const p = await ensurePanel();
        if (!isOpen) return; // closed while loading
        p.open();
        emitter.emit('open', undefined);
      } catch {
        setOpenState(false);
      }
    },

    async openConversation(conversationId, opts = {}) {
      if (!config || !UUID_RE.test(String(conversationId ?? ''))) return false;
      if (!host) {
        // arm not known yet: replayed by mountLauncher() (dropped for good in the holdout arm)
        pendingResume = opts.token ? { conversation_id: conversationId, token: opts.token } : null;
        return false;
      }
      await api.open();
      try {
        const p = await ensurePanel();
        const ok = p.openConversation ? await p.openConversation(conversationId, opts) : false;
        if (!ok) emitter.emit('error', { code: 'resume_failed', message: 'conversation could not be opened' });
        else pageViews?.notify();
        return ok;
      } catch (err) {
        emitter.emit('error', { code: 'resume_failed', message: err instanceof Error ? err.message : String(err) });
        return false;
      }
    },

    close() {
      if (!isOpen) return;
      // closed by the visitor after clicking a link that did not leave the page: nothing to reopen
      if (config) clearReopen(config.tenantKey);
      setOpenState(false);
      panel?.close();
      emitter.emit('close', undefined);
      launcher?.el.focus();
    },

    async sendMessage(text: string) {
      if (!config) {
        console.warn('[AICommerce] sendMessage() called before init()');
        return;
      }
      const content = String(text ?? '').trim();
      if (!content || !host) return;
      await api.open();
      const p = await ensurePanel();
      await p.sendMessage(content);
    },

    on(event, cb) {
      return emitter.on(event, cb);
    },

    off(event, cb) {
      emitter.off(event, cb);
    },

    destroy() {
      measurement?.stop();
      measurement = null;
      pageViews?.stop();
      pageViews = null;
      cleanTitle = null;
      for (const off of launcherTeardown.splice(0)) off();
      stopBottomBars?.();
      stopBottomBars = null;
      stopKeyboard?.();
      stopKeyboard = null;
      panel?.unmount();
      launcher?.destroy();
      host?.remove();
      panel = null;
      panelPromise = null;
      launcher = null;
      host = null;
      root = null;
      shadow = null;
      config = null;
      pendingResume = null;
      pendingReopen = false;
      isOpen = false;
      for (const off of attributeTeardown.splice(0)) off();
      lastCartAttributes = '';
      emitter.clear();
    },
  };

  return api;
}
